/**
 * PdfPreviewModal — in-app viewer for a freshly exported report PDF.
 *
 * Opens the moment the Cloud Function returns the URL, with no popup
 * involved: the user never leaves CliniKit and nothing can be popup-blocked
 * (a delayed window.open after the async render is — the click's transient
 * activation expires long before Chromium finishes). The "כרטיסייה חדשה"
 * button is a real anchor, so a user click there always opens.
 *
 * Esc / overlay click / close button dismiss it. Body scroll is locked
 * while open.
 */

import { useEffect } from "react";
import { FilePdf, ArrowSquareOut } from "@phosphor-icons/react";
import ModalCloseButton from "../../../ui/ModalCloseButton";

function PdfPreviewModal({ url, title = "הדוח מוכן", onClose }) {
  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [url, onClose]);

  if (!url) return null;

  return (
    <div className="prw-pdfmodal-overlay" onClick={onClose}>
      <div
        className="prw-pdfmodal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="prw-pdfmodal-header">
          <div className="prw-pdfmodal-header-text">
            <span className="prw-pdfmodal-icon">
              <FilePdf size={18} weight="fill" />
            </span>
            <div>
              <span className="prw-pdfmodal-eyebrow">PDF</span>
              <h3 className="prw-pdfmodal-title">{title}</h3>
            </div>
          </div>
          <div className="prw-pdfmodal-actions">
            <a
              className="prw-btn ghost prw-pdfmodal-open"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="פתיחה בכרטיסייה חדשה (משם אפשר גם לשמור ולהדפיס)"
            >
              <ArrowSquareOut size={15} />
              <span className="prw-pdfmodal-open-label">כרטיסייה חדשה</span>
            </a>
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>
        <div className="prw-pdfmodal-body">
          <iframe src={url} title={title} className="prw-pdfmodal-frame" />
        </div>
      </div>
    </div>
  );
}

export default PdfPreviewModal;
